import { Injectable } from '@angular/core';
import { User } from '../model/user';
import { LoginResponse } from '../model/login.response';
import { plainToClass } from 'class-transformer';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from 'src/app/service/user.service';
import { Event } from '../model/event';
import { AdminService } from './admin.service';

@Injectable({
  providedIn: 'root'
})
export class InfoService {
  private event: Event;
  private _user: User;
  private _proxyUser: User;
  private _proxyRefresh: string;
  private _proxyToken: string;
  private _proxyActive = false;

  // Getter/setter pra que toda mudança de `proxyActive` toggle a classe
  // `proxy-active` no `<body>`. Permite que regras de chrome global
  // consumam a custom property `--chrome-top-offset` (definida em
  // `_core.scss` gated por `body.proxy-active`) — single source of
  // truth pra altura agregada do chrome acima do canvas. Substitui
  // ~10 regras `&.proxy-active { top: ... }` espalhadas pelos
  // componentes de chrome.
  get proxyActive(): boolean { return this._proxyActive; }
  set proxyActive(v: boolean) {
    this._proxyActive = v;
    if (typeof document !== 'undefined' && document.body) {
      document.body.classList.toggle('proxy-active', v);
    }
  }

  constructor(
    public adminService: AdminService,
    public translate: TranslateService,
    public userService: UserService,
  ) {
    if (this.token) {
      this._user = this.getUserFromService();
    }
    this._proxyRefresh = localStorage.getItem('user_proxy_refresh');
    this._proxyToken = localStorage.getItem('user_proxy_token');
    if(this._proxyToken){
      this._user = this.getUserFromService();
    }
    this._proxyUser = this.getUserFromStorage('user_proxy');
    if (this._proxyUser) {
      this.proxyActive = true;
    }
  }

  set user(u: User) {
    this._user = u;
    this.setPreferencesLocalCache();
  }

  get user(): User {
    if(this._user !== undefined){
      return this._user;
    } else {
      this.userService.getUserFromToken().subscribe(u => {
        this._user = u;
      }, () => {
        return this._user;
      },
      () => {
          return this._user;
      });
    }
  }

  getUserFromService(): User {
    if(this._user !== undefined){
      return this._user;
    }else{
      this.userService.getUserFromToken().subscribe((u) => {
        this._user = u;
      });
      return this._user;
    }
  }

  getUserFromStorage(key = 'user'): User {
    const u = JSON.parse(localStorage.getItem(key));
    if (u) {
      return plainToClass(User, u as User);
    }
    return undefined;
  }
  
  set selectedEvent(event: Event) {
    this.event = event;
  }

  get selectedEvent(): Event {
    return this.event;
  }

  getPreference(key: string): string | number | boolean {
    let preferences = JSON.parse(localStorage.getItem('preferences'));
    if (!preferences) {
      // Tenta popular o cache (só rola se o user já estiver carregado no
      // service). Re-lê depois pra que a PRIMEIRA chamada bem-sucedida
      // já devolva o valor real, e não `undefined`.
      this.setPreferencesLocalCache();
      preferences = JSON.parse(localStorage.getItem('preferences'));
    }
    // Retorna `false` em vez de `undefined` quando a chave não existe —
    // evita `ExpressionChangedAfterItHasBeenCheckedError` em bindings
    // booleanos (ex.: [ngClass] em CardPlaceholderComponent), onde o
    // primeiro tick via `undefined` e o segundo via o valor real,
    // fazendo Angular detectar mudança entre check + checkNoChanges.
    return preferences && preferences[key] !== undefined ? preferences[key] : false;
  }

  setPreference(key: string, value: any): void  {
    let preferences = JSON.parse(localStorage.getItem('preferences'));
    if (preferences) {
      this.setPreferencesLocalCache();
      preferences = JSON.parse(localStorage.getItem('preferences'));
    }
    preferences[key] = value;
    localStorage.setItem('preferences', JSON.stringify(preferences));
  }

  setPreferencesLocalCache(): void {
    let user = this.getUserFromService();

    if (user) {
      let preferences = `{"viewMode": "${user.profile.viewMode}",` +
        `"statusIcon":${user.profile.statusIcon},` +
        `"statusColorAsBackgroundColor":${user.profile.statusColorAsBackgroundColor}}`
      localStorage.setItem('preferences', preferences);
    }
  }

  set response(res: LoginResponse) {
    this.token = res.token;
    this.refresh = res.refresh;
    this.user = res.user;

    this.translate.use(res.user.profile.language);
  }

  set refresh(token: string) {
    localStorage.setItem('refresh', token);
  }

  get refresh(): string {
    return localStorage.getItem('refresh');
  }

  set token(token: string) {
    localStorage.setItem('token', token);
  }

  get token(): string {
    return localStorage.getItem('token');
  }

  setProxyUser(res: LoginResponse) {
    // Backup user before proxy.
    localStorage.setItem('user_proxy', JSON.stringify(this.user));
    localStorage.setItem('user_proxy_refresh', this.refresh);
    localStorage.setItem('user_proxy_token', this.token);
    this._proxyUser = this.user;
    this._proxyToken = this.token;
    this._proxyRefresh = this.refresh;

    // Set new user as active user.
    this.user = res.user;
    this.refresh = res.refresh;
    this.token = res.token;
    this.proxyActive = true;
  }

  clearProxy(user?: User) {
    // If proxy was active, restore previous user and token.
    // ELse will do nothing and just clear any garbage, just in case.
    this.user = this._proxyUser ? this._proxyUser : this.user;
    this.refresh = this._proxyRefresh ? this._proxyRefresh : this.refresh;
    this.token = this._proxyToken ? this._proxyToken : this.token;

    if (user) {
      this.adminService.clearProxyUser(user.id).subscribe();
    }
 
    this.proxyActive = false;
    localStorage.removeItem('user_proxy');
    localStorage.removeItem('user_proxy_token');
  }
}
